import { Location } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from '@angular/forms';
import { BaseComponent } from '@core/base.component';
import { ICON } from '@core/util/icon';
import { getInitials } from '@core/util/string';
import { BusinessUserRole, Invite } from '@models/businesses';
import { BusinessSignUpRequestModel } from '@models/businessSignUpRequests';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import { UrlService } from '@services/url/url.service';
import { AuthState } from '@state/auth';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { LandscapePositions } from '@state/auth/auth.models';
import { UserProfileState } from '@state/user-profile';
import { CreateStatusType } from '@state/utils/models';
import { startCase } from 'lodash';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

const USER_INFORMATION = {
	FIRST_NAME: 'firstName',
	LAST_NAME: 'lastName',
	INITIALS: 'initials',
	ROLE: 'role',
	PHONE_NUMBER: 'phoneNumber',
} as const;

export interface UserProfileForm {
	[USER_INFORMATION.FIRST_NAME]: string;
	[USER_INFORMATION.LAST_NAME]: string;
	[USER_INFORMATION.INITIALS]: string;
	[USER_INFORMATION.ROLE]: BusinessUserRole;
	[USER_INFORMATION.PHONE_NUMBER]?: string;
}

interface VM {
	formData: UserProfileForm;
	submitted: boolean;
}

const defaultVM: VM = {
	formData: null,
	submitted: false,
};

@Component({
	selector: 'app-create-user-profile',
	templateUrl: './create-user-profile.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CreateUserProfileComponent
	extends BaseComponent<VM>
	implements OnInit
{
	@Input() set formData(formData: UserProfileForm) {
		this.VM = { formData };
	}
	@Input() onlyProfile = false;

	@Output() onSubmit: EventEmitter<UserProfileForm> = new EventEmitter();

	public authForm: FormGroup;
	public USER_INFORMATION = USER_INFORMATION;
	public createStatusTypes = CreateStatusType;
	public roles = BusinessUserRole;
	public roleOptions = Object.values(BusinessUserRole).map((role) => ({
		name: startCase(role.toLowerCase()),
		value: role,
	}));
	public landscapePositions = LandscapePositions;
	public icon = ICON;
	public displayName: string;

	@ViewSelectSnapshot(AuthState.getLoading) authLoading: boolean;
	@ViewSelectSnapshot(UserProfileState.getCreateStatus)
	createStatus: CreateStatusType;
	@Select(AuthState.getInvite) invite$: Observable<Invite>;
	@Select(AuthState.getSignUpRequest)
	signUpRequest$: Observable<BusinessSignUpRequestModel>;

	constructor(
		private formBuilder: FormBuilder,
		private navService: NavigationService,
		private authDispatch: AuthDispatch,
		private location: Location,
		private store: Store,
	) {
		super(defaultVM);
	}

	public ngOnInit(): void {
		this.authForm = this.createNewProfileForm();
		if (this.onlyProfile) {
			this.authForm.removeControl(USER_INFORMATION.PHONE_NUMBER);
		}

		this.signUpRequest$
			.pipe(
				filter((request) => !!request),
				take(1),
			)
			.subscribe((request) => {
				this.VM = {
					formData: {
						...this.VM.formData,
						...{
							[USER_INFORMATION.FIRST_NAME]: request.firstName,
							[USER_INFORMATION.LAST_NAME]: request.lastName,
							[USER_INFORMATION.PHONE_NUMBER]: request.phoneNumber,
							[USER_INFORMATION.INITIALS]: getInitials(
								[request.firstName, request.lastName].join(' '),
							),
						},
					},
				};
			});
	}

	get firstName(): AbstractControl {
		return this.form[USER_INFORMATION.FIRST_NAME];
	}

	get lastName(): AbstractControl {
		return this.form[USER_INFORMATION.LAST_NAME];
	}

	get initials(): AbstractControl {
		return this.form[USER_INFORMATION.INITIALS];
	}

	get role(): AbstractControl {
		return this.form[USER_INFORMATION.ROLE];
	}

	get phoneNumber(): AbstractControl {
		return this.form[USER_INFORMATION.PHONE_NUMBER];
	}

	get form(): { [key: string]: AbstractControl } {
		return this.authForm.controls;
	}

	get contactUrl(): string {
		return UrlService.Contact();
	}

	get legalUrl(): string {
		return UrlService.Legal();
	}

	logOut() {
		if (this.store.selectSnapshot(UserProfileState.getCurrentBusinessId)) {
			this.authDispatch.LogOut();
		} else {
			this.location.back();
		}
	}

	private createNewProfileForm(): FormGroup {
		return this.formBuilder.group({
			[USER_INFORMATION.FIRST_NAME]: new FormControl('', [Validators.required]),
			[USER_INFORMATION.LAST_NAME]: new FormControl('', [Validators.required]),
			[USER_INFORMATION.INITIALS]: new FormControl('', [Validators.required]),
			[USER_INFORMATION.ROLE]: new FormControl('', [Validators.required]),
			[USER_INFORMATION.PHONE_NUMBER]: new FormControl(''),
		});
	}

	public onNameEnter(): void {
		const initials = getInitials(
			[this.firstName.value, this.lastName.value].join(' '),
		);
		this.initials.setValue(initials);
	}

	public onSubmitForm(): void {
		this.VM = {
			submitted: true,
		};
		if (this.authForm.valid) {
			const formData: UserProfileForm = {
				firstName: this.firstName.value,
				lastName: this.lastName.value,
				initials: this.initials.value,
				role: this.role.value,
				...(!this.onlyProfile && { phoneNumber: this.phoneNumber.value }),
			};
			this.onSubmit.emit(formData);
		} else {
			this.authForm.markAllAsTouched();
		}
	}

	public updateLandscapePosition(position: LandscapePositions): void {
		this.authDispatch.SetLandscapePosition(position);
	}

	public onSelectRole(
		roleResult: DropdownResult<DropdownOption<BusinessUserRole>>,
	): void {
		this.role.setValue(roleResult.result.value);
	}

	goToLogin(): void {
		this.navService.goToLogin();
	}
}
